import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import posthogLib from 'posthog-js';
import { usePostHog } from 'posthog-js/react';
import { useLocation } from 'react-router';

import useUserDetails from '@/hooks/useUserDetails';
import { useImpersonatorEmailFromStorage } from '@/services/storage';

interface PostHogContextType {
  identifyUser: (email: string) => void;
}

const PostHogContext = createContext<PostHogContextType | undefined>(undefined);

export const IdentifyPostHogProvider = ({ children }: { children: ReactNode }) => {
  const distinctId = useRef<string>(null);
  const { hash } = useLocation();
  const { userEmail, hsObjectId, currentSubscription } = useUserDetails();
  const impersonatorEmail = useImpersonatorEmailFromStorage();
  const posthog = usePostHog();

  useEffect(() => {
    if (hash && !distinctId.current) {
      const hashParams = new URLSearchParams(hash.substring(1));
      distinctId.current = hashParams.get('distinct_id');
    }
  }, [hash]);

  const identifyUser = useCallback(
    (email: string) => {
      if (!posthog) return;

      // Convert email to lowercase to ensure consistency
      const lowercaseEmail = email.toLowerCase();

      if (distinctId.current && lowercaseEmail) {
        posthogLib.alias(lowercaseEmail, distinctId.current);
      }
      posthogLib.identify(lowercaseEmail, {
        hubSpotUserId: hsObjectId,
        email: lowercaseEmail
      });

      posthogLib.capture('$set', {
        $set: {
          userAccessLevel: currentSubscription === 'n/a' ? null : currentSubscription
        }
      });
    },
    [posthog, hsObjectId, currentSubscription]
  );

  useEffect(() => {
    if (impersonatorEmail) {
      identifyUser(impersonatorEmail);
    } else if (userEmail) {
      identifyUser(userEmail);
    }
  }, [identifyUser, userEmail, impersonatorEmail]);

  const value = useMemo(
    () => ({
      identifyUser
    }),
    [identifyUser]
  );

  return <PostHogContext value={value}>{children}</PostHogContext>;
};

export const usePostHogContext = () => {
  const context = useContext(PostHogContext);
  if (!context) throw new Error('usePostHogContext must be used within PostHogContext');
  return context;
};
